import * as THREE from 'three';
import { CalculatePosition } from './types';
import { isOrthographicCamera, isPerspectiveCamera } from './three';

const v1 = /* @__PURE__ */ new THREE.Vector3();
const v2 = /* @__PURE__ */ new THREE.Vector3();
const v3 = /* @__PURE__ */ new THREE.Vector3();
const v4 = /* @__PURE__ */ new THREE.Vector2();

/**
 * Calculates the position of an object in 2D space based on its 3D position and the camera's projection.
 */
export const calculatePosition: CalculatePosition = (el, camera, size) => {
  const pos = v1.setFromMatrixPosition(el.matrixWorld).project(camera);
  return [
    pos.x * size.width * 0.5 + size.width * 0.5,
    -pos.y * size.height * 0.5 + size.height * 0.5,
  ];
};

/**
 * Checks if an object is behind the camera.
 * This is useful for determining if an annotation should be visible or not.
 */
export const isObjectBehindCamera = (el: THREE.Object3D, camera: THREE.Camera) => {
  const obj = v1.setFromMatrixPosition(el.matrixWorld);
  const cam = v2.setFromMatrixPosition(camera.matrixWorld);
  return obj.sub(cam).angleTo(camera.getWorldDirection(v3)) > Math.PI / 2;
};

/**
 * Calculates the scale of an object based on its distance from the camera.
 * Determines how large the object should appear in a 2D overlay.
 */
export const objectScale = (el: THREE.Object3D, camera: THREE.Camera) => {
  if (isOrthographicCamera(camera)) return camera.zoom;
  if (isPerspectiveCamera(camera)) {
    const obj = v1.setFromMatrixPosition(el.matrixWorld);
    const cam = v2.setFromMatrixPosition(camera.matrixWorld);
    const vFOV = (camera.fov * Math.PI) / 180;
    const dist = obj.distanceTo(cam);
    return 1 / (2 * Math.tan(vFOV / 2) * dist);
  }
  return 1;
};

/**
 * Calculates the z-index of an object based on its distance from the camera.
 * This is useful for rendering objects in the correct order in a 2D overlay.
 */
export const objectZIndex = (
  el: THREE.Object3D,
  camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
  [max, min]: [number, number],
) => {
  const obj = v1.setFromMatrixPosition(el.matrixWorld);
  const cam = v2.setFromMatrixPosition(camera.matrixWorld);
  const A = (min - max) / (camera.far - camera.near);
  const B = min - A * camera.far;
  return Math.round(A * obj.distanceTo(cam) + B);
};

/**
 * Determines if an object is visible in the scene based on its position, camera, and occlusion objects.
 * This function uses raycasting to check if the object is occluded by any other objects in the scene.
 */
export const isObjectVisible = (
  el: THREE.Object3D,
  camera: THREE.Camera,
  raycaster: THREE.Raycaster,
  occlude: THREE.Object3D[],
) => {
  const elPos = v1.setFromMatrixPosition(el.matrixWorld);
  const screenPos = elPos.clone();
  screenPos.project(camera);
  v4.set(screenPos.x, screenPos.y);
  raycaster.setFromCamera(v4, camera);
  const intersects = raycaster.intersectObjects(occlude, true);
  if (intersects.length) {
    const intersectionDistance = intersects[0].distance;
    const pointDistance = elPos.distanceTo(raycaster.ray.origin);
    return pointDistance < intersectionDistance;
  }
  return true;
};

/**
 * A utility function to handle small floating-point values.
 * It returns 0 if the absolute value is less than a small epsilon value, otherwise returns the original value.
 */
export const epsilon = (value: number) => (Math.abs(value) < 1e-10 ? 0 : value);

/**
 * Generates a CSS matrix3d string from a THREE.Matrix4 and a set of multipliers
 * for applying transformations in a CSS context, such as in a React component
 */
export const cssMatrixWith = (matrix: THREE.Matrix4, multipliers: THREE.Matrix4Tuple) => {
  let matrix3d = 'matrix3d(';
  for (let i = 0; i < 16; i++) {
    matrix3d += epsilon(multipliers[i] * matrix.elements[i]) + (i !== 15 ? ',' : ')');
  }
  return matrix3d;
};

/**
 * Builds a set of CSS transform multipliers for an object.
 */
export const buildObjectCssMultipliers = (factor: number): THREE.Matrix4Tuple => {
  // prettier-ignore
  return [
     1 / factor,  1 / factor,  1 / factor, 1,
    -1 / factor, -1 / factor, -1 / factor, -1,
     1 / factor,  1 / factor,  1 / factor, 1,
                1,           1,          1, 1,
  ];
};

/**
 * Converts a THREE.Matrix4 to a CSS matrix3d string for object transformations.
 *
 * @link https://github.com/mrdoob/three.js/blob/0ea870f6d70be2886fa035d204404b5448f3c6a6/examples/jsm/renderers/CSS3DRenderer.js#L323C12-L323C30
 */
export function getObjectCSSMatrix(m: THREE.Matrix4, factor = 1): string {
  return cssMatrixWith(m, buildObjectCssMultipliers(factor));
}

// prettier-ignore
const CAMERA_MATRIX_MULTIPLIERS: THREE.Matrix4Tuple = [
  1, -1, 1, 1,
  1, -1, 1, 1,
  1, -1, 1, 1,
  1, -1, 1, 1,
];

/**
 * Converts a THREE.Matrix4 to a CSS matrix3d string for camera transformations.
 *
 * @link https://github.com/mrdoob/three.js/blob/0ea870f6d70be2886fa035d204404b5448f3c6a6/examples/jsm/renderers/CSS3DRenderer.js#L298
 */
export function getCameraCSSMatrix(matrix: THREE.Matrix4): string {
  return cssMatrixWith(matrix, CAMERA_MATRIX_MULTIPLIERS);
}
